import { formatChallengeRating } from '@/utils/challengeRating';

export type WildShapeTransformAction = 'action' | 'bonus';

export type WildShapeOverride = {
  max_cr?: number;
  min_max_cr?: number;
  max_cr_per_level?: number;
  allow_swim?: boolean;
  allow_fly?: boolean;
  extra_creature_types?: string[];
  extra_creature_types_level?: number;
  extra_form_ids?: string[];
  extra_form_ids_level?: number;
  extra_form_uses_cost?: number;
  max_uses?: number | 'unlimited';
  transform_action?: WildShapeTransformAction;
};

export type WildShapeRules = {
  druidLevel: number;
  maxCR: number;
  allowSwim: boolean;
  allowFly: boolean;
  creatureTypes: string[];
  extraFormIds: string[];
  extraFormUsesCost: number;
  maxUses: number | 'unlimited';
  transformAction: WildShapeTransformAction;
  durationHours: number;
};

export type WildShapeLockReason = 'type' | 'cr' | 'fly' | 'swim';

export type WildShapeEligibility = {
  eligible: boolean;
  addable: boolean;
  lockReason?: string;
  lockKind?: WildShapeLockReason;
};

const MOON_ELEMENTALS = ['air-elemental', 'earth-elemental', 'fire-elemental', 'water-elemental'];

/** Named PHB-style profiles. Applied when subclass name matches; does not ship Moon as SRD data. */
export const WILD_SHAPE_SUBCLASS_PROFILES: Record<string, WildShapeOverride> = {
  moon: {
    min_max_cr: 1,
    max_cr_per_level: 1 / 3,
    transform_action: 'bonus',
    extra_form_ids: MOON_ELEMENTALS,
    extra_form_ids_level: 10,
    extra_form_uses_cost: 2,
  },
  'circle of the moon': {
    min_max_cr: 1,
    max_cr_per_level: 1 / 3,
    transform_action: 'bonus',
    extra_form_ids: MOON_ELEMENTALS,
    extra_form_ids_level: 10,
    extra_form_uses_cost: 2,
  },
};

export function parseChallengeRating(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw !== 'string') return 0;
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  if (trimmed.includes('/')) {
    const [a, b] = trimmed.split('/');
    const n = Number(a);
    const d = Number(b);
    if (Number.isFinite(n) && Number.isFinite(d) && d !== 0) return n / d;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

export function monsterChallengeRating(monster: unknown): number {
  const rec = asRecord(monster);
  if (!rec) return 0;
  const data = asRecord(rec.data) ?? rec;
  return parseChallengeRating(data.challenge_rating ?? data.cr ?? rec.challenge_rating ?? rec.cr);
}

export function monsterCreatureType(monster: unknown): string {
  const rec = asRecord(monster);
  if (!rec) return '';
  const data = asRecord(rec.data) ?? rec;
  const raw = data.type ?? rec.type;
  if (typeof raw === 'string') return raw.trim().toLowerCase().split(/[\s,(]/)[0] ?? '';
  const obj = asRecord(raw);
  if (obj) {
    const name = obj.name ?? obj.type ?? obj.index;
    if (typeof name === 'string') return name.trim().toLowerCase().split(/[\s,(]/)[0] ?? '';
  }
  return '';
}

export function monsterId(monster: unknown): string {
  const rec = asRecord(monster);
  if (!rec) return '';
  const meta = asRecord(rec.meta);
  const data = asRecord(rec.data);
  const id = rec.id ?? rec.slug ?? meta?.id ?? meta?.slug ?? data?.id ?? data?.slug;
  return id != null ? String(id) : '';
}

export function monsterName(monster: unknown): string {
  const rec = asRecord(monster);
  if (!rec) return 'Beast';
  const meta = asRecord(rec.meta);
  const data = asRecord(rec.data);
  const name = rec.name ?? meta?.name ?? data?.name;
  return typeof name === 'string' && name.trim() ? name.trim() : 'Beast';
}

export type MonsterSpeedFlags = {
  fly: boolean;
  swim: boolean;
  walkFt?: number;
  flyFt?: number;
  swimFt?: number;
};

export function monsterSpeedFlags(monster: unknown): MonsterSpeedFlags {
  const rec = asRecord(monster);
  const data = rec ? asRecord(rec.data) ?? rec : null;
  const s = data?.speed ?? data?.speeds ?? rec?.speed ?? rec?.speeds;
  if (s == null) return { fly: false, swim: false };
  if (typeof s === 'object') {
    const obj = s as Record<string, unknown>;
    const flyFt = numericSpeed(obj.fly ?? obj.flying);
    const swimFt = numericSpeed(obj.swim ?? obj.swimming);
    const walkFt = numericSpeed(obj.walk ?? obj.walking);
    return {
      fly: flyFt != null && flyFt > 0,
      swim: swimFt != null && swimFt > 0,
      walkFt: walkFt ?? undefined,
      flyFt: flyFt ?? undefined,
      swimFt: swimFt ?? undefined,
    };
  }
  const str = String(s);
  const fly = /fly/i.test(str);
  const swim = /swim/i.test(str);
  return { fly, swim };
}

export function getDruidLevel(character: unknown): number {
  const rec = asRecord(character);
  if (!rec) return 0;
  const multi = rec.multiclassLevels ?? rec.multiclass_levels;
  if (multi && typeof multi === 'object') {
    for (const [key, value] of Object.entries(multi as Record<string, unknown>)) {
      if (key.trim().toLowerCase() === 'druid') {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? n : 0;
      }
    }
  }
  const className = String(rec.class ?? rec.class_name ?? rec.classData?.name ?? '').toLowerCase();
  if (className.includes('druid')) {
    const n = Number(rec.level ?? rec.total_level ?? 1);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }
  return 0;
}

/** PHB Beast Shapes table (2014), ignoring subclass overlays. */
export function getBaseWildShapeRules(druidLevel: number): WildShapeRules {
  const level = Number.isFinite(druidLevel) ? Math.max(0, Math.floor(druidLevel)) : 0;
  let maxCR = 0;
  let allowSwim = false;
  let allowFly = false;
  if (level >= 8) {
    maxCR = 1;
    allowSwim = true;
    allowFly = true;
  } else if (level >= 4) {
    maxCR = 0.5;
    allowSwim = true;
  } else if (level >= 2) {
    maxCR = 0.25;
  }
  return {
    druidLevel: level,
    maxCR,
    allowSwim,
    allowFly,
    creatureTypes: ['beast'],
    extraFormIds: [],
    extraFormUsesCost: 1,
    maxUses: level >= 20 ? 'unlimited' : level >= 2 ? 2 : 0,
    transformAction: 'action',
    durationHours: level >= 2 ? Math.max(1, Math.floor(level / 2)) : 0,
  };
}

export function getWildShapeRules(character: unknown): WildShapeRules {
  const druidLevel = getDruidLevel(character);
  let rules = getBaseWildShapeRules(druidLevel);
  const rec = asRecord(character);
  const overlays: WildShapeOverride[] = [];

  const subclassData = rec ? asRecord(rec.subclassData) ?? asRecord(rec.subclass_data) : null;
  if (subclassData?.wild_shape && typeof subclassData.wild_shape === 'object') {
    overlays.push(subclassData.wild_shape as WildShapeOverride);
  }

  overlays.push(...collectFeatureWildShapeGrants(rec, druidLevel));

  const subclassName = normalizeSubclassName(
    subclassData?.name ?? rec?.subclass ?? rec?.subclass_name
  );
  const profile = WILD_SHAPE_SUBCLASS_PROFILES[subclassName];
  if (profile) overlays.push(profile);

  for (const overlay of overlays) {
    rules = mergeWildShapeOverlay(rules, overlay, druidLevel);
  }
  return rules;
}

export function wildShapeEligibilityLabel(rules: WildShapeRules): string {
  const cr = formatChallengeRating(rules.maxCR);
  const swim = rules.allowSwim ? 'swim' : 'no swim';
  const fly = rules.allowFly ? 'fly' : 'no fly';
  return `CR ${cr} · ${swim} · ${fly}`;
}

export function evaluateWildShapeCandidate(
  monster: unknown,
  rules: WildShapeRules,
  options?: { includeLocked?: boolean }
): WildShapeEligibility {
  const includeLocked = options?.includeLocked === true;
  const type = monsterCreatureType(monster);
  const id = monsterId(monster);
  const extraForm = rules.extraFormIds.some((extra) => extra === id.trim().toLowerCase());
  const typeAllowed = extraForm || rules.creatureTypes.includes(type);

  if (!typeAllowed) {
    return { eligible: false, addable: false, lockReason: 'not a valid Wild Shape type', lockKind: 'type' };
  }

  if (extraForm) {
    return { eligible: true, addable: true };
  }

  const cr = monsterChallengeRating(monster);
  const speeds = monsterSpeedFlags(monster);
  let lockKind: WildShapeLockReason | undefined;
  let lockReason: string | undefined;

  if (cr > rules.maxCR + 1e-9) {
    lockKind = 'cr';
    lockReason = `CR ${formatChallengeRating(cr)}`;
  } else if (speeds.fly && !rules.allowFly) {
    lockKind = 'fly';
    lockReason = 'needs fly';
  } else if (speeds.swim && !rules.allowSwim) {
    lockKind = 'swim';
    lockReason = 'needs swim';
  }

  const eligible = !lockKind;
  return {
    eligible,
    addable: eligible || includeLocked,
    lockReason,
    lockKind,
  };
}

export function filterMonstersForWildShape(
  monsters: unknown[],
  rules: WildShapeRules,
  options?: { includeLocked?: boolean }
): unknown[] {
  return (Array.isArray(monsters) ? monsters : []).filter((m) => {
    return evaluateWildShapeCandidate(m, rules, options).addable;
  });
}

export function extraFormUsesCost(rules: WildShapeRules, beastId: string): number {
  if (rules.extraFormIds.includes(String(beastId).trim().toLowerCase())) {
    return Math.max(1, rules.extraFormUsesCost || 1);
  }
  return 1;
}

function mergeWildShapeOverlay(
  base: WildShapeRules,
  overlay: WildShapeOverride,
  druidLevel: number
): WildShapeRules {
  let maxCR = base.maxCR;
  if (typeof overlay.min_max_cr === 'number') maxCR = Math.max(maxCR, overlay.min_max_cr);
  if (typeof overlay.max_cr_per_level === 'number' && overlay.max_cr_per_level > 0) {
    maxCR = Math.max(maxCR, Math.floor(druidLevel * overlay.max_cr_per_level));
  }
  if (typeof overlay.max_cr === 'number') maxCR = Math.max(maxCR, overlay.max_cr);

  const extraTypesLevel = overlay.extra_creature_types_level ?? 0;
  const extraTypes =
    Array.isArray(overlay.extra_creature_types) && druidLevel >= extraTypesLevel
      ? overlay.extra_creature_types.map((t) => String(t).toLowerCase())
      : [];

  const extraIdsLevel = overlay.extra_form_ids_level ?? overlay.extra_creature_types_level ?? 0;
  const extraIds =
    Array.isArray(overlay.extra_form_ids) && druidLevel >= extraIdsLevel
      ? overlay.extra_form_ids.map(String)
      : [];

  let maxUses = base.maxUses;
  if (overlay.max_uses === 'unlimited') maxUses = 'unlimited';
  else if (typeof overlay.max_uses === 'number') {
    maxUses = maxUses === 'unlimited' ? 'unlimited' : Math.max(maxUses, overlay.max_uses);
  }

  return {
    ...base,
    maxCR,
    allowSwim: base.allowSwim || overlay.allow_swim === true,
    allowFly: base.allowFly || overlay.allow_fly === true,
    creatureTypes: uniqueStrings([...base.creatureTypes, ...extraTypes]),
    extraFormIds: uniqueStrings([...base.extraFormIds, ...extraIds]),
    extraFormUsesCost: overlay.extra_form_uses_cost ?? base.extraFormUsesCost,
    maxUses,
    transformAction: overlay.transform_action ?? base.transformAction,
  };
}

function collectFeatureWildShapeGrants(
  character: Record<string, unknown> | null,
  druidLevel: number
): WildShapeOverride[] {
  if (!character) return [];
  const bags = [character.classData, character.subclassData, character.class_data, character.subclass_data];
  const out: WildShapeOverride[] = [];
  for (const bag of bags) {
    const rec = asRecord(bag);
    const levels = rec?.features_by_level;
    if (!Array.isArray(levels)) continue;
    for (const row of levels) {
      const levelRow = asRecord(row);
      const lvl = Number(levelRow?.level ?? 0);
      if (lvl > druidLevel) continue;
      const features = Array.isArray(levelRow?.features) ? levelRow.features : [];
      for (const feature of features) {
        const grants = asRecord(asRecord(feature)?.grants);
        const wild = grants?.wild_shape;
        if (wild && typeof wild === 'object') out.push(wild as WildShapeOverride);
      }
    }
  }
  return out;
}

function normalizeSubclassName(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/^circle of the\s+/, '')
    .replace(/^circle of\s+/, '');
}

function numericSpeed(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const match = raw.match(/(\d+)/);
    if (match) return Number(match[1]);
  }
  return null;
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const key = value.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
